/**
 * Tool Test Panel Component
 * 
 * Allows direct tool execution for debugging/testing.
 * Toggle visibility with Ctrl+Shift+T.
 * 
 * NOTE: Development/testing only - should be disabled in production.
 */

import * as React from "react";
import { makeStyles, tokens, Button, Text, Dropdown, Option, Textarea, Spinner, Divider, Input, Checkbox } from "@fluentui/react-components";
import { Play20Regular, Delete20Regular, Copy20Regular, Image20Regular, Add20Regular, Subtract20Regular, Table20Regular, Camera20Regular, ArrowDownload20Regular } from "@fluentui/react-icons";

// Import tools for direct execution
import { CreateOrEditShapeTool } from "../../main/agents/liveEditor/tools/createOrEditShape/createOrEditShape.tool";
import { CreateOrEditTableTool } from "../../main/agents/liveEditor/tools/createOrEditTable/createOrEditTable.tool";
import { CreateOrEditChartTool } from "../../main/agents/liveEditor/tools/createOrEditChart/createOrEditChart.tool";
import { executeBuildSection } from "../../main/agents/tools/liveBuildArea/liveBuildArea.execution";
import { backendClient } from "../../services/communication/backendClient";

const useStyles = makeStyles({
    container: {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "95vw",
        maxWidth: "800px",
        maxHeight: "90vh",
        backgroundColor: tokens.colorNeutralBackground1,
        border: `2px solid ${tokens.colorBrandBackground}`,
        borderRadius: "8px",
        boxShadow: tokens.shadow64,
        zIndex: 10000,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
    },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 16px",
        backgroundColor: tokens.colorBrandBackground,
        color: tokens.colorNeutralForegroundOnBrand,
    },
    title: {
        fontWeight: 700,
        fontSize: "14px",
    },
    content: {
        flex: 1,
        overflowY: "auto",
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
    },
    label: {
        fontWeight: 600,
        fontSize: "12px",
        color: tokens.colorNeutralForeground1,
        marginBottom: "4px",
    },
    textareaWrapper: {
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: "300px",
    },
    textarea: {
        fontFamily: "Consolas, Monaco, monospace",
        fontSize: "11px",
        width: "100%",
        flex: 1,
        "& textarea": {
            minHeight: "280px !important",
            height: "100% !important",
            fontFamily: "Consolas, Monaco, monospace !important",
            fontSize: "11px !important",
        },
    },
    resultArea: {
        backgroundColor: tokens.colorNeutralBackground3,
        borderRadius: "4px",
        padding: "12px",
        fontFamily: "Consolas, Monaco, monospace",
        fontSize: "11px",
        maxHeight: "250px",
        overflowY: "auto",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
    },
    successResult: {
        borderLeft: `4px solid ${tokens.colorPaletteGreenBorder2}`,
    },
    errorResult: {
        borderLeft: `4px solid ${tokens.colorPaletteRedBorder2}`,
    },
    actions: {
        display: "flex",
        gap: "8px",
        padding: "12px 16px",
        borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
        backgroundColor: tokens.colorNeutralBackground2,
    },
    presets: {
        display: "flex",
        gap: "4px",
        flexWrap: "wrap",
        marginTop: "4px",
    },
    presetButton: {
        fontSize: "10px",
    },
    tableTestSection: {
        backgroundColor: tokens.colorNeutralBackground3,
        borderRadius: "8px",
        padding: "12px",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
    },
    tableTestHeader: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        marginBottom: "4px",
    },
    tableTestRow: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        flexWrap: "wrap",
    },
    tableTestInfo: {
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: "4px",
        padding: "8px 12px",
        fontSize: "11px",
        fontFamily: "Consolas, Monaco, monospace",
        flex: 1,
    },
    tableTestButtons: {
        display: "flex",
        gap: "4px",
        flexWrap: "wrap",
    },
    inputSmall: {
        width: "60px",
    },
});

type ToolName = "createOrEditShape" | "buildSection" | "geminiImageGen" | "createOrEditTable" | "createOrEditChart";

interface TestResult {
    success: boolean;
    data: unknown;
    timestamp: string;
}

/**
 * Comprehensive test execution record for export
 */
export interface TestExecutionRecord {
    testId: string;
    timestamp: string;
    tool: string;
    params: object;
    beforeScreenshot: string;  // base64
    afterScreenshot: string;   // base64
    result: { success: boolean; data: unknown };
    ojsLogs: string[];         // Office.js console logs
    comLogs: string[];         // Python COM logs (fetched from backend)
    errors: string[];
    durationMs: number;
    slideId: number;
}

interface GeminiTestResult {
    success: boolean;
    model_used: string;
    image_base64?: string;
    text_response?: string;
    error?: string;
    raw_response?: Record<string, unknown>;
}

// Preset test payloads
const PRESETS: Record<string, { tool: ToolName; params: object; description: string }> = {
    // ═══════════════════════════════════════════════════════════════════════════
    // SHAPE CREATION PRESETS
    // ═══════════════════════════════════════════════════════════════════════════
    "shape-no-outline": {
        tool: "createOrEditShape",
        description: "Rectangle NO outline",
        params: {
            slideId: 1,
            operationType: "create",
            shapeType: "rectangle",
            position: { left: 100, top: 100, width: 200, height: 100 },
            fill: { type: "solid", color: "#4472C4" },
            outline: { null: true },
        },
    },
    "shape-with-outline": {
        tool: "createOrEditShape",
        description: "Rectangle WITH outline",
        params: {
            slideId: 1,
            operationType: "create",
            shapeType: "rectangle",
            position: { left: 100, top: 250, width: 200, height: 100 },
            fill: { type: "solid", color: "#70AD47" },
            outline: { color: "#FF0000", weight: 2 },
        },
    },
    "shape-textbox": {
        tool: "createOrEditShape",
        description: "Textbox with text",
        params: {
            slideId: 1,
            operationType: "create",
            shapeType: "textbox",
            position: { left: 100, top: 100, width: 300, height: 80 },
            fill: { type: "solid", color: "#FFFFFF" },
            textContent: {
                paragraphOperations: [
                    { operation: "insert", index: 0, paragraph: { setText: "Hello World!", textFormat: [{ text: "Hello World!", fontSize: 24, bold: true }] } }
                ]
            }
        },
    },
    "shape-ellipse": {
        tool: "createOrEditShape",
        description: "Ellipse/Circle",
        params: {
            slideId: 1,
            operationType: "create",
            shapeType: "ellipse",
            position: { left: 350, top: 100, width: 150, height: 150 },
            fill: { type: "solid", color: "#ED7D31", transparency: 0.2 },
            outline: { color: "#C00000", weight: 3 },
        },
    },
    "shape-triangle": {
        tool: "createOrEditShape",
        description: "Triangle",
        params: {
            slideId: 1,
            operationType: "create",
            shapeType: "triangle",
            position: { left: 100, top: 300, width: 120, height: 100 },
            fill: { type: "solid", color: "#5B9BD5" },
            outline: { null: true },
        },
    },
    "shape-arrow": {
        tool: "createOrEditShape",
        description: "Arrow shape",
        params: {
            slideId: 1,
            operationType: "create",
            shapeType: "arrow",
            position: { left: 250, top: 300, width: 150, height: 60 },
            fill: { type: "solid", color: "#70AD47" },
            outline: { null: true },
        },
    },
    "shape-line": {
        tool: "createOrEditShape",
        description: "Line/Connector",
        params: {
            slideId: 1,
            operationType: "create",
            shapeType: "line",
            position: { left: 100, top: 450, width: 300, height: 0 },
            outline: { color: "#000000", weight: 2 },
        },
    },
    // ═══════════════════════════════════════════════════════════════════════════
    // TABLE PRESETS
    // ═══════════════════════════════════════════════════════════════════════════
    "table-3x4-lorem": {
        tool: "createOrEditTable",
        description: "📊 Table 3x4 Basic",
        params: {
            slideId: 1,
            operationType: "create",
            position: { left: 50, top: 100, width: 600, height: 200 },
            tableConfig: {
                rows: 3,
                columns: 4,
                cellText: [
                    { cell: { row: 0, col: 0 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "Header A", textFormat: [{ text: "Header A", bold: true }] } }] } },
                    { cell: { row: 0, col: 1 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "Header B", textFormat: [{ text: "Header B", bold: true }] } }] } },
                    { cell: { row: 0, col: 2 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "Header C", textFormat: [{ text: "Header C", bold: true }] } }] } },
                    { cell: { row: 0, col: 3 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "Header D", textFormat: [{ text: "Header D", bold: true }] } }] } },
                    { cell: { row: 1, col: 0 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "Lorem ipsum" } }] } },
                    { cell: { row: 1, col: 1 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "dolor sit" } }] } },
                    { cell: { row: 1, col: 2 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "amet consectetur" } }] } },
                    { cell: { row: 1, col: 3 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "adipiscing" } }] } },
                    { cell: { row: 2, col: 0 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "elit sed" } }] } },
                    { cell: { row: 2, col: 1 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "do eiusmod" } }] } },
                    { cell: { row: 2, col: 2 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "tempor incididunt" } }] } },
                    { cell: { row: 2, col: 3 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "ut labore" } }] } },
                ],
            },
        },
    },
    "table-5x3-data": {
        tool: "createOrEditTable",
        description: "📊 Table 5x3 Data",
        params: {
            slideId: 1,
            operationType: "create",
            position: { left: 100, top: 150, width: 500, height: 250 },
            tableConfig: {
                rows: 5,
                columns: 3,
                cellText: [
                    { cell: { row: 0, col: 0 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "Company", textFormat: [{ text: "Company", bold: true }] } }] } },
                    { cell: { row: 0, col: 1 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "Revenue", textFormat: [{ text: "Revenue", bold: true }] } }] } },
                    { cell: { row: 0, col: 2 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "Growth", textFormat: [{ text: "Growth", bold: true }] } }] } },
                    { cell: { row: 1, col: 0 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "Acme Corp" } }] } },
                    { cell: { row: 1, col: 1 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "$1.2B" } }] } },
                    { cell: { row: 1, col: 2 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "+15%" } }] } },
                    { cell: { row: 2, col: 0 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "Beta Inc" } }] } },
                    { cell: { row: 2, col: 1 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "$800M" } }] } },
                    { cell: { row: 2, col: 2 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "+8%" } }] } },
                    { cell: { row: 3, col: 0 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "Gamma LLC" } }] } },
                    { cell: { row: 3, col: 1 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "$500M" } }] } },
                    { cell: { row: 3, col: 2 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "+22%" } }] } },
                    { cell: { row: 4, col: 0 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "Delta Co" } }] } },
                    { cell: { row: 4, col: 1 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "$350M" } }] } },
                    { cell: { row: 4, col: 2 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "-3%" } }] } },
                ],
            },
        },
    },
    "table-with-formatting": {
        tool: "createOrEditTable",
        description: "📊 Table with Cell Formatting",
        params: {
            slideId: 1,
            operationType: "create",
            position: { left: 100, top: 150, width: 500, height: 180 },
            tableConfig: {
                rows: 4,
                columns: 3,
                cellText: [
                    { cell: { row: 0, col: 0 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "Product", textFormat: [{ text: "Product", bold: true }] } }] } },
                    { cell: { row: 0, col: 1 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "Status", textFormat: [{ text: "Status", bold: true }] } }] } },
                    { cell: { row: 0, col: 2 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "Score", textFormat: [{ text: "Score", bold: true }] } }] } },
                    { cell: { row: 1, col: 0 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "Widget A" } }] } },
                    { cell: { row: 1, col: 1 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "Active" } }] } },
                    { cell: { row: 1, col: 2 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "95%" } }] } },
                    { cell: { row: 2, col: 0 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "Widget B" } }] } },
                    { cell: { row: 2, col: 1 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "Warning" } }] } },
                    { cell: { row: 2, col: 2 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "72%" } }] } },
                    { cell: { row: 3, col: 0 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "Widget C" } }] } },
                    { cell: { row: 3, col: 1 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "Critical" } }] } },
                    { cell: { row: 3, col: 2 }, textContent: { paragraphOperations: [{ operation: "insert", index: 0, paragraph: { setText: "45%" } }] } },
                ],
                cellFormatting: [
                    { cellRange: { startRow: 0, startCol: 0, endRow: 0, endCol: 2 }, fillColor: "#4472C4", textColor: "#FFFFFF" },
                    { cellRange: { startRow: 1, startCol: 1, endRow: 1, endCol: 1 }, fillColor: "#C6EFCE", textColor: "#006100" },
                    { cellRange: { startRow: 2, startCol: 1, endRow: 2, endCol: 1 }, fillColor: "#FFEB9C", textColor: "#9C5700" },
                    { cellRange: { startRow: 3, startCol: 1, endRow: 3, endCol: 1 }, fillColor: "#FFC7CE", textColor: "#9C0006" },
                ],
            },
        },
    },
    // ═══════════════════════════════════════════════════════════════════════════
    // CHART PRESETS
    // ═══════════════════════════════════════════════════════════════════════════
    "chart-column-basic": {
        tool: "createOrEditChart",
        description: "📊 Column Chart (Basic)",
        params: {
            slideId: 1,
            operationType: "create",
            position: { left: 100, top: 150, width: 400, height: 300 },
            chartConfig: {
                chartType: "column",
                data: {
                    categories: { values: ["Q1", "Q2", "Q3", "Q4"] },
                    series: [
                        { name: "Revenue", values: [100, 120, 150, 180] },
                        { name: "Profit", values: [20, 25, 35, 45] }
                    ]
                },
                legend: { position: "bottom" }
            }
        },
    },
    "chart-bar-stacked": {
        tool: "createOrEditChart",
        description: "📊 Bar Stacked",
        params: {
            slideId: 1,
            operationType: "create",
            position: { left: 100, top: 150, width: 400, height: 300 },
            chartConfig: {
                chartType: "bar_stacked",
                data: {
                    categories: { values: ["Product A", "Product B", "Product C"] },
                    series: [
                        { name: "2023", values: [50, 80, 60] },
                        { name: "2024", values: [70, 90, 85] },
                        { name: "2025", values: [90, 100, 110] }
                    ]
                },
                legend: { position: "right" }
            }
        },
    },
    "chart-line-markers": {
        tool: "createOrEditChart",
        description: "📈 Line with Markers",
        params: {
            slideId: 1,
            operationType: "create",
            position: { left: 100, top: 150, width: 400, height: 300 },
            chartConfig: {
                chartType: "line_markers",
                data: {
                    categories: { values: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"] },
                    series: [
                        { name: "Visitors", values: [1000, 1200, 1100, 1500, 1800, 2200] },
                        { name: "Conversions", values: [50, 65, 55, 80, 100, 130] }
                    ]
                },
                lineOptions: { smoothLines: true },
                legend: { position: "bottom" }
            }
        },
    },
    "chart-pie-basic": {
        tool: "createOrEditChart",
        description: "🥧 Pie Chart",
        params: {
            slideId: 1,
            operationType: "create",
            position: { left: 100, top: 150, width: 350, height: 300 },
            chartConfig: {
                chartType: "pie",
                data: {
                    categories: { values: ["Marketing", "Sales", "R&D", "Operations", "HR"] },
                    series: [
                        { name: "Budget", values: [25, 35, 20, 15, 5] }
                    ]
                },
                pieOptions: { rotation: 0 },
                legend: { position: "right" }
            }
        },
    },
    "chart-doughnut": {
        tool: "createOrEditChart",
        description: "🍩 Doughnut Chart",
        params: {
            slideId: 1,
            operationType: "create",
            position: { left: 100, top: 150, width: 350, height: 300 },
            chartConfig: {
                chartType: "doughnut",
                data: {
                    categories: { values: ["Completed", "In Progress", "Not Started"] },
                    series: [
                        { name: "Tasks", values: [65, 25, 10] }
                    ]
                },
                pieOptions: { holeSize: 50 },
                legend: { position: "bottom" }
            }
        },
    },
    "chart-scatter": {
        tool: "createOrEditChart",
        description: "⚬ Scatter Chart",
        params: {
            slideId: 1,
            operationType: "create",
            position: { left: 100, top: 150, width: 400, height: 300 },
            chartConfig: {
                chartType: "scatter",
                data: {
                    categories: { values: [10, 20, 30, 40, 50, 60, 70, 80] },
                    series: [
                        { name: "Performance", values: [15, 25, 22, 45, 55, 48, 72, 85] }
                    ]
                },
                legend: { position: "none" }
            }
        },
    },
    "chart-area-stacked": {
        tool: "createOrEditChart",
        description: "📊 Area Stacked",
        params: {
            slideId: 1,
            operationType: "create",
            position: { left: 100, top: 150, width: 400, height: 300 },
            chartConfig: {
                chartType: "area_stacked",
                data: {
                    categories: { values: ["2020", "2021", "2022", "2023", "2024"] },
                    series: [
                        { name: "North", values: [100, 120, 140, 160, 200] },
                        { name: "South", values: [80, 90, 100, 130, 150] },
                        { name: "West", values: [60, 75, 85, 100, 120] }
                    ]
                },
                legend: { position: "bottom" }
            }
        },
    },
    "chart-combo": {
        tool: "createOrEditChart",
        description: "📊 Combo (Column + Line)",
        params: {
            slideId: 1,
            operationType: "create",
            position: { left: 100, top: 150, width: 450, height: 300 },
            chartConfig: {
                chartType: "combo",
                data: {
                    categories: { values: ["Q1", "Q2", "Q3", "Q4"] },
                    series: [
                        { name: "Revenue", values: [100, 120, 150, 180], chartType: "column" },
                        { name: "Growth %", values: [10, 15, 20, 25], chartType: "line", axisGroup: "secondary" }
                    ]
                },
                legend: { position: "bottom" }
            }
        },
    },
    "chart-waterfall": {
        tool: "createOrEditChart",
        description: "📊 Waterfall Chart",
        params: {
            slideId: 1,
            operationType: "create",
            position: { left: 100, top: 150, width: 450, height: 300 },
            chartConfig: {
                chartType: "waterfall",
                data: {
                    categories: { values: ["Start", "Product A", "Product B", "Costs", "End"] },
                    series: [
                        { name: "Revenue", values: [100, 50, 30, -40, 140] }
                    ]
                },
                legend: { position: "none" }
            }
        },
    },
    "chart-radar": {
        tool: "createOrEditChart",
        description: "📊 Radar Chart",
        params: {
            slideId: 1,
            operationType: "create",
            position: { left: 100, top: 150, width: 350, height: 300 },
            chartConfig: {
                chartType: "radar",
                data: {
                    categories: { values: ["Quality", "Price", "Features", "Support", "Design"] },
                    series: [
                        { name: "Product A", values: [80, 70, 90, 60, 85] },
                        { name: "Product B", values: [65, 90, 70, 80, 60] }
                    ]
                },
                legend: { position: "bottom" }
            }
        },
    },
    // ═══════════════════════════════════════════════════════════════════════════
    // BUILD SECTION PRESETS
    // ═══════════════════════════════════════════════════════════════════════════
    "buildSection-no-outline": {
        tool: "buildSection",
        description: "BuildSection NO outline",
        params: {
            sectionId: "test_section",
            slideId: 1,
            targetArea: { left: 10, top: 10, width: 30, height: 30 },
            collections: [
                {
                    type: "createShape",
                    shapeType: "rectangle",
                    position: { left: 350, top: 100, width: 150, height: 80 },
                    fill: { type: "solid", color: "#ED7D31" },
                    outline: { null: true },
                },
            ],
        },
    },
    "buildSection-image-no-outline": {
        tool: "buildSection",
        description: "BuildSection image fill",
        params: {
            sectionId: "image_test",
            slideId: 1,
            targetArea: { left: 10, top: 10, width: 80, height: 50 },
            collections: [
                {
                    type: "createShape",
                    shapeType: "rectangle",
                    position: { left: 350, top: 250, width: 150, height: 80 },
                    fill: { type: "image", imageSource: "test_image" },
                    outline: { null: true },
                },
            ],
        },
    },
    // ═══════════════════════════════════════════════════════════════════════════
    // GEMINI IMAGE GENERATION PRESETS
    // ═══════════════════════════════════════════════════════════════════════════
    "gemini-2-flash": {
        tool: "geminiImageGen",
        description: "🖼️ Gemini 2.0 Flash",
        params: {
            model: "gemini-2.0-flash-exp",
            prompt: "Generate a simple wireframe sketch of a PowerPoint slide with a title at the top, a chart on the left side, and bullet points on the right side. Use clean lines and a professional business style.",
        },
    },
    "gemini-2-pro": {
        tool: "geminiImageGen",
        description: "🖼️ Gemini 2.0 Pro",
        params: {
            model: "gemini-2.0-pro-exp",
            prompt: "Generate a simple wireframe sketch of a PowerPoint slide with a title at the top, a chart on the left side, and bullet points on the right side. Use clean lines and a professional business style.",
        },
    },
};

// Global visibility state
let globalTestPanelVisible = false;
let globalDevModeEnabled = false;  // Set via admin panel
const testPanelListeners = new Set<() => void>();

function notifyTestPanelListeners() {
    testPanelListeners.forEach(fn => fn());
}

export function toggleToolTestPanel() {
    globalTestPanelVisible = !globalTestPanelVisible;
    console.log(`[ToolTestPanel] Manual toggle visibility: ${globalTestPanelVisible}`);
    notifyTestPanelListeners();
}

export function isToolTestPanelVisible(): boolean {
    return globalTestPanelVisible || globalDevModeEnabled;
}

/**
 * Set dev mode enabled/disabled (called from App.tsx when admin panel toggles)
 */
export function setDevModeEnabled(enabled: boolean) {
    globalDevModeEnabled = enabled;
    console.log(`[ToolTestPanel] Dev mode enabled: ${enabled}`);
    notifyTestPanelListeners();
}

/**
 * Check if dev mode is enabled
 */
export function isDevModeEnabled(): boolean {
    return globalDevModeEnabled;
}

export const ToolTestPanel: React.FC = () => {
    const styles = useStyles();
    const [isVisible, setIsVisible] = React.useState(isToolTestPanelVisible());
    const [selectedTool, setSelectedTool] = React.useState<ToolName>("createOrEditShape");
    const [inputJson, setInputJson] = React.useState(JSON.stringify(PRESETS["shape-no-outline"].params, null, 2));
    const [result, setResult] = React.useState<TestResult | null>(null);
    const [isExecuting, setIsExecuting] = React.useState(false);
    
    // Table testing state
    const [tableShapeId, setTableShapeId] = React.useState<number | null>(null);
    const [tableSlideId, setTableSlideId] = React.useState(1);
    const [tableRows, setTableRows] = React.useState(3);
    const [tableCols, setTableCols] = React.useState(4);
    const [autoResize, setAutoResize] = React.useState(true);  // Auto-resize toggle
    const [tableFontSize, setTableFontSize] = React.useState(14);  // Font size for table text
    
    // Table Style Copy state
    const [sourceTableSlide, setSourceTableSlide] = React.useState(1);
    const [sourceTableShape, setSourceTableShape] = React.useState<number | null>(null);
    const [targetTableSlide, setTargetTableSlide] = React.useState(1);
    const [targetTableShape, setTargetTableShape] = React.useState<number | null>(null);
    const [extractedStyleInfo, setExtractedStyleInfo] = React.useState<string | null>(null);
    
    // Screenshot capture state
    const [captureScreenshots, setCaptureScreenshots] = React.useState(true);
    const [lastTestRecord, setLastTestRecord] = React.useState<TestExecutionRecord | null>(null);
    const ojsLogsRef = React.useRef<string[]>([]);

    // Subscribe to global visibility state (includes both manual toggle and dev mode)
    React.useEffect(() => {
        const listener = () => setIsVisible(isToolTestPanelVisible());
        testPanelListeners.add(listener);
        return () => { testPanelListeners.delete(listener); };
    }, []);

    // Console log interceptor for capturing OJS logs during test execution
    const startLogCapture = React.useCallback(() => {
        ojsLogsRef.current = [];
        const originalLog = console.log;
        const originalWarn = console.warn;
        const originalError = console.error;
        
        console.log = (...args) => {
            ojsLogsRef.current.push(`[LOG] ${args.map(a => typeof a === 'object' ? JSON.stringify(a) : String(a)).join(' ')}`);
            originalLog.apply(console, args);
        };
        console.warn = (...args) => {
            ojsLogsRef.current.push(`[WARN] ${args.map(a => typeof a === 'object' ? JSON.stringify(a) : String(a)).join(' ')}`);
            originalWarn.apply(console, args);
        };
        console.error = (...args) => {
            ojsLogsRef.current.push(`[ERROR] ${args.map(a => typeof a === 'object' ? JSON.stringify(a) : String(a)).join(' ')}`);
            originalError.apply(console, args);
        };
        
        return () => {
            console.log = originalLog;
            console.warn = originalWarn;
            console.error = originalError;
        };
    }, []);

    // Capture current slide screenshot using Office.js
    const captureSlideScreenshot = React.useCallback(async (slideId: number): Promise<string> => {
        let imageBase64 = '';
        await PowerPoint.run(async (context) => {
            const slides = context.presentation.slides;
            slides.load('items');
            await context.sync();
            
            // Get slide by index (slideId is 1-based)
            const slideIndex = slideId - 1;
            if (slideIndex >= 0 && slideIndex < slides.items.length) {
                const slide = slides.items[slideIndex];
                const imageResult = slide.getImageAsBase64();
                await context.sync();
                imageBase64 = imageResult.value || '';
            } else {
                console.warn(`[ToolTestPanel] Slide index ${slideIndex} out of bounds (${slides.items.length} slides)`);
            }
        });
        return imageBase64;
    }, []);

    // Fetch COM logs from backend
    const fetchCOMLogs = React.useCallback(async (sinceTimestamp?: string): Promise<string[]> => {
        try {
            const url = sinceTimestamp 
                ? `${backendClient.baseUrl}/debug/com-logs?since=${encodeURIComponent(sinceTimestamp)}`
                : `${backendClient.baseUrl}/debug/com-logs`;
            const response = await fetch(url);
            if (response.ok) {
                const data = await response.json();
                return data.logs || [];
            }
        } catch (error) {
            console.warn('[ToolTestPanel] Could not fetch COM logs:', error);
        }
        return [];
    }, []);

    // Keyboard shortcut: Ctrl+Shift+T
    React.useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.ctrlKey && e.shiftKey && e.key === 'T') {
                e.preventDefault();
                toggleToolTestPanel();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const loadPreset = (presetKey: string) => {
        const preset = PRESETS[presetKey];
        if (preset) {
            setSelectedTool(preset.tool);
            setInputJson(JSON.stringify(preset.params, null, 2));
            setResult(null);
        }
    };

    const executeTest = async () => {
        setIsExecuting(true);
        setResult(null);
        setLastTestRecord(null);

        const testId = `test_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
        const startTime = Date.now();
        const startTimestamp = new Date().toISOString();
        const errors: string[] = [];
        
        // Start capturing console logs
        const stopLogCapture = startLogCapture();

        try {
            const params = JSON.parse(inputJson);
            console.log(`[ToolTestPanel] Executing ${selectedTool} with:`, params);

            // Extract slideId from params for screenshot capture
            const slideId = params.slideId || 1;

            // Capture BEFORE screenshot
            let beforeScreenshot = '';
            if (captureScreenshots) {
                console.log(`[ToolTestPanel] Capturing BEFORE screenshot for slide ${slideId}`);
                beforeScreenshot = await captureSlideScreenshot(slideId);
                console.log(`[ToolTestPanel] BEFORE screenshot captured: ${beforeScreenshot.length} chars`);
            }

            let toolResult: unknown;

            if (selectedTool === "createOrEditShape") {
                const tool = new CreateOrEditShapeTool();
                toolResult = await tool.execute(params);
            } else if (selectedTool === "buildSection") {
                toolResult = await executeBuildSection(params);
            } else if (selectedTool === "geminiImageGen") {
                // Call backend endpoint to test Gemini image generation
                toolResult = await testGeminiImageGeneration(params);
            } else if (selectedTool === "createOrEditTable") {
                const tool = new CreateOrEditTableTool();
                toolResult = await tool.execute(params);
            } else if (selectedTool === "createOrEditChart") {
                const tool = new CreateOrEditChartTool();
                toolResult = await tool.execute(params);
            }

            // Capture AFTER screenshot
            let afterScreenshot = '';
            if (captureScreenshots) {
                // Small delay to ensure PowerPoint has updated
                await new Promise(resolve => setTimeout(resolve, 500));
                console.log(`[ToolTestPanel] Capturing AFTER screenshot for slide ${slideId}`);
                afterScreenshot = await captureSlideScreenshot(slideId);
                console.log(`[ToolTestPanel] AFTER screenshot captured: ${afterScreenshot.length} chars`);
            }

            // Stop log capture
            stopLogCapture();

            // Fetch COM logs from backend
            const comLogs = await fetchCOMLogs(startTimestamp);

            const durationMs = Date.now() - startTime;
            console.log(`[ToolTestPanel] Result:`, toolResult);

            // Create comprehensive test record
            const testRecord: TestExecutionRecord = {
                testId,
                timestamp: startTimestamp,
                tool: selectedTool,
                params,
                beforeScreenshot,
                afterScreenshot,
                result: { success: true, data: toolResult },
                ojsLogs: [...ojsLogsRef.current],
                comLogs,
                errors,
                durationMs,
                slideId,
            };
            setLastTestRecord(testRecord);

            setResult({
                success: true,
                data: toolResult,
                timestamp: new Date().toLocaleTimeString(),
            });
        } catch (error) {
            // Stop log capture
            stopLogCapture();

            const errorMessage = error instanceof Error ? error.message : String(error);
            errors.push(errorMessage);
            console.error(`[ToolTestPanel] Error:`, error);

            // Still try to capture after screenshot on error
            let afterScreenshot = '';
            let slideId = 1;
            try {
                const params = JSON.parse(inputJson);
                slideId = params.slideId || 1;
                if (captureScreenshots) {
                    afterScreenshot = await captureSlideScreenshot(slideId);
                }
            } catch (screenshotError) {
                console.warn('[ToolTestPanel] Could not capture after screenshot on error:', screenshotError);
            }

            // Fetch COM logs even on error
            const comLogs = await fetchCOMLogs(startTimestamp);

            const durationMs = Date.now() - startTime;

            // Create test record even for failed tests
            const testRecord: TestExecutionRecord = {
                testId,
                timestamp: startTimestamp,
                tool: selectedTool,
                params: (() => { try { return JSON.parse(inputJson); } catch { return {}; } })(),
                beforeScreenshot: '',
                afterScreenshot,
                result: { success: false, data: errorMessage },
                ojsLogs: [...ojsLogsRef.current],
                comLogs,
                errors,
                durationMs,
                slideId,
            };
            setLastTestRecord(testRecord);

            setResult({
                success: false,
                data: errorMessage,
                timestamp: new Date().toLocaleTimeString(),
            });
        } finally {
            setIsExecuting(false);
        }
    };

    // Test Gemini image generation via backend
    const testGeminiImageGeneration = async (params: { model: string; prompt: string }): Promise<GeminiTestResult> => {
        // Get auth header from shared authService  
        const { authService } = await import("@carousel-office/shared");
        const authHeader = authService.getAuthHeader();
        
        const response = await fetch(`${backendClient.baseUrl}/debug/test-gemini-image-generation`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': authHeader,
            },
            body: JSON.stringify(params),
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText}`);
        }
        
        const result = await response.json() as GeminiTestResult;
        
        // If we got an image, log it
        if (result.success && result.image_base64) {
            console.log(`[ToolTestPanel] Gemini generated image! Length: ${result.image_base64.length}`);
        }
        
        return result;
    };

    const copyResult = () => {
        if (result) {
            navigator.clipboard.writeText(JSON.stringify(result.data, null, 2));
        }
    };

    // Export test results as JSON file
    const exportTestResults = React.useCallback(() => {
        if (!lastTestRecord) {
            console.warn('[ToolTestPanel] No test record to export');
            return;
        }

        try {
            // Create export object with all data
            const exportData = {
                ...lastTestRecord,
                exportedAt: new Date().toISOString(),
            };

            // Convert to JSON string
            const jsonString = JSON.stringify(exportData, null, 2);
            
            // Create blob and download link
            const blob = new Blob([jsonString], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            
            // Create temporary link and trigger download
            const link = document.createElement('a');
            link.href = url;
            link.download = `test-result-${lastTestRecord.tool}-${lastTestRecord.testId}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Cleanup
            URL.revokeObjectURL(url);
            
            console.log('[ToolTestPanel] Test results exported successfully');
        } catch (error) {
            console.error('[ToolTestPanel] Failed to export test results:', error);
        }
    }, [lastTestRecord]);

    // ═══════════════════════════════════════════════════════════════════════════
    // TABLE TESTING HELPERS
    // ═══════════════════════════════════════════════════════════════════════════
    
    const LOREM_WORDS = [
        "Lorem", "ipsum", "dolor", "sit", "amet", "consectetur", "adipiscing", "elit",
        "sed", "do", "eiusmod", "tempor", "incididunt", "ut", "labore", "et", "dolore",
        "magna", "aliqua", "enim", "ad", "minim", "veniam", "quis", "nostrud",
    ];

    const generateLoremCell = () => {
        const wordCount = Math.floor(Math.random() * 3) + 1;
        const words: string[] = [];
        for (let i = 0; i < wordCount; i++) {
            words.push(LOREM_WORDS[Math.floor(Math.random() * LOREM_WORDS.length)]);
        }
        return words.join(' ');
    };

    const createTestTable = async () => {
        setIsExecuting(true);
        try {
            const cellText = [];
            for (let r = 0; r < tableRows; r++) {
                for (let c = 0; c < tableCols; c++) {
                    const isHeader = r === 0;
                    const text = isHeader ? `Header ${String.fromCharCode(65 + c)}` : generateLoremCell();
                    cellText.push({
                        cell: { row: r, col: c },
                        textContent: {
                            paragraphOperations: [{
                                operation: "insert",
                                index: 0,
                                paragraph: { 
                                    setText: text, 
                                    textFormat: [{ text, bold: isHeader, fontSize: tableFontSize }]
                                }
                            }]
                        }
                    });
                }
            }

            // Calculate row height based on font size (font size + padding)
            const rowHeight = Math.max(tableFontSize + 16, 30);
            
            // cellFormatting uses Python model structure: {cellRange, fontSize, bold, fillColor, textColor}
            // Note: This bypasses TypeScript schema validation since we call tool.execute() directly
            const params = {
                slideId: tableSlideId,
                operationType: "create",
                position: { left: 50, top: 100, width: 600, height: tableRows * rowHeight },
                tableConfig: { 
                    rows: tableRows, 
                    columns: tableCols, 
                    cellText,
                    // Apply formatting via COM backend (Python CellFormattingModel structure)
                    cellFormatting: [
                        // Apply font size to ALL cells (body)
                        {
                            cellRange: { startRow: 1, startCol: 0, endRow: tableRows - 1, endCol: tableCols - 1 },
                            fontSize: tableFontSize,
                            bold: false
                        },
                        // Apply bold + font size to header row
                        {
                            cellRange: { startRow: 0, startCol: 0, endRow: 0, endCol: tableCols - 1 },
                            fontSize: tableFontSize,
                            bold: true
                        }
                    ]
                }
            };

            const tool = new CreateOrEditTableTool();
            const toolResult = await tool.execute(params);
            
            // Store shapeId for future operations
            if (toolResult.success && toolResult.shapeId) {
                setTableShapeId(toolResult.shapeId);
            }
            
            setResult({
                success: true,
                data: toolResult,
                timestamp: new Date().toLocaleTimeString(),
            });
        } catch (error) {
            setResult({
                success: false,
                data: error instanceof Error ? error.message : String(error),
                timestamp: new Date().toLocaleTimeString(),
            });
        } finally {
            setIsExecuting(false);
        }
    };

    const selectTableFromSlide = async () => {
        setIsExecuting(true);
        try {
            await PowerPoint.run(async (context) => {
                // Get selected shapes
                const selection = context.presentation.getSelectedShapes();
                selection.load("items");
                await context.sync();

                if (selection.items.length === 0) {
                    throw new Error("No shape selected. Please select a table on the slide first.");
                }

                const selectedShape = selection.items[0];
                selectedShape.load(["id", "type"]);
                await context.sync();

                // Check if it's a table
                if (selectedShape.type !== PowerPoint.ShapeType.table) {
                    throw new Error(`Selected shape is not a table (type: ${selectedShape.type}). Please select a table.`);
                }

                // Get table dimensions
                // Note: Using type assertion since getTable() may not be in all type definitions
                const table = (selectedShape as { getTable(): PowerPoint.Table }).getTable();
                table.load(["rowCount", "columnCount"]);
                await context.sync();

                // Get current slide index
                const slide = context.presentation.getSelectedSlides().getItemAt(0);
                slide.load("id");
                const slides = context.presentation.slides;
                slides.load("items");
                await context.sync();

                // Find slide index (1-based)
                let slideIndex = 1;
                for (let i = 0; i < slides.items.length; i++) {
                    slides.items[i].load("id");
                }
                await context.sync();
                
                for (let i = 0; i < slides.items.length; i++) {
                    if (slides.items[i].id === slide.id) {
                        slideIndex = i + 1;
                        break;
                    }
                }

                // Update state with selected table info
                const shapeId = parseInt(selectedShape.id, 10);
                setTableShapeId(shapeId);
                setTableSlideId(slideIndex);
                setTableRows(table.rowCount);
                setTableCols(table.columnCount);

                setResult({
                    success: true,
                    data: {
                        message: "Table selected successfully!",
                        shapeId,
                        slideId: slideIndex,
                        rows: table.rowCount,
                        columns: table.columnCount,
                    },
                    timestamp: new Date().toLocaleTimeString(),
                });
            });
        } catch (error) {
            setResult({
                success: false,
                data: error instanceof Error ? error.message : String(error),
                timestamp: new Date().toLocaleTimeString(),
            });
        } finally {
            setIsExecuting(false);
        }
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // TABLE STYLE COPY FUNCTIONS
    // ═══════════════════════════════════════════════════════════════════════════
    
    const getSelectedTableInfo = async (targetField: 'source' | 'target') => {
        setIsExecuting(true);
        try {
            await PowerPoint.run(async (context) => {
                const selection = context.presentation.getSelectedShapes();
                selection.load("items");
                await context.sync();

                if (selection.items.length === 0) {
                    throw new Error("No shape selected. Please select a table.");
                }

                const selectedShape = selection.items[0];
                selectedShape.load(["id", "type"]);
                await context.sync();

                if (selectedShape.type !== PowerPoint.ShapeType.table) {
                    throw new Error(`Selected shape is not a table (type: ${selectedShape.type})`);
                }

                // Get current slide
                const slide = context.presentation.getSelectedSlides().getItemAt(0);
                slide.load("id");
                const slides = context.presentation.slides;
                slides.load("items");
                await context.sync();

                let slideIndex = 1;
                for (let i = 0; i < slides.items.length; i++) {
                    slides.items[i].load("id");
                }
                await context.sync();
                for (let i = 0; i < slides.items.length; i++) {
                    if (slides.items[i].id === slide.id) {
                        slideIndex = i + 1;
                        break;
                    }
                }

                const shapeId = parseInt(selectedShape.id, 10);
                
                if (targetField === 'source') {
                    setSourceTableSlide(slideIndex);
                    setSourceTableShape(shapeId);
                } else {
                    setTargetTableSlide(slideIndex);
                    setTargetTableShape(shapeId);
                }

                setResult({
                    success: true,
                    data: { message: `${targetField} table selected`, shapeId, slideId: slideIndex },
                    timestamp: new Date().toLocaleTimeString(),
                });
            });
        } catch (error) {
            setResult({
                success: false,
                data: error instanceof Error ? error.message : String(error),
                timestamp: new Date().toLocaleTimeString(),
            });
        } finally {
            setIsExecuting(false);
        }
    };

    const extractTableStyle = async () => {
        if (!sourceTableShape) {
            setResult({ success: false, data: "Select a source table first", timestamp: new Date().toLocaleTimeString() });
            return;
        }
        
        setIsExecuting(true);
        try {
            const response = await fetch(`${backendClient.baseUrl}/com/copyTableStyle`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    documentId: 'test_deck',
                    slideId: sourceTableSlide,
                    sourceSlideId: sourceTableSlide,
                    sourceShapeId: sourceTableShape,
                    copyWhat: ['all'],
                    detectPatterns: true,
                }),
            });
            
            const data = await response.json();
            
            if (data.success) {
                const info = [];
                if (data.rows) info.push(`${data.rows.length} rows`);
                if (data.columns) info.push(`${data.columns.length} cols`);
                if (data.styling?.rows?.length) info.push(`${data.styling.rows.length} row style(s)`);
                if (data.styling?.alternatingRows?.detected) info.push('alternating');
                setExtractedStyleInfo(info.join(' • '));
            }
            
            setResult({ success: data.success, data, timestamp: new Date().toLocaleTimeString() });
        } catch (error) {
            setResult({ success: false, data: error instanceof Error ? error.message : String(error), timestamp: new Date().toLocaleTimeString() });
        } finally {
            setIsExecuting(false);
        }
    };

    const applyTableStyle = async () => {
        if (!sourceTableShape || !targetTableShape) {
            setResult({ success: false, data: "Select both source and target tables", timestamp: new Date().toLocaleTimeString() });
            return;
        }
        
        setIsExecuting(true);
        try {
            const response = await fetch(`${backendClient.baseUrl}/com/copyTableStyle`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    documentId: 'test_deck',
                    slideId: sourceTableSlide,
                    sourceSlideId: sourceTableSlide,
                    sourceShapeId: sourceTableShape,
                    targetSlideId: targetTableSlide,
                    targetShapeId: targetTableShape,
                    copyWhat: ['all'],
                    detectPatterns: true,
                }),
            });
            
            const data = await response.json();
            setResult({ success: data.success && data.applied, data, timestamp: new Date().toLocaleTimeString() });
        } catch (error) {
            setResult({ success: false, data: error instanceof Error ? error.message : String(error), timestamp: new Date().toLocaleTimeString() });
        } finally {
            setIsExecuting(false);
        }
    };

    const executeTableOperation = async (operation: 'addRow' | 'removeRow' | 'addCol' | 'removeCol') => {
        if (!tableShapeId) {
            setResult({
                success: false,
                data: "No table created yet. Create a table first!",
                timestamp: new Date().toLocaleTimeString(),
            });
            return;
        }

        setIsExecuting(true);
        try {
            let params: Record<string, unknown> = {
                slideId: tableSlideId,
                operationType: "edit",
                shapeId: tableShapeId,
                autoResize: autoResize,  // Pass auto-resize toggle
                tableConfig: {},
            };

            if (operation === 'addRow') {
                params.tableConfig = {
                    rowOperations: [{ operation: "insert", index: tableRows, count: 1 }]
                };
                setTableRows(prev => prev + 1);
            } else if (operation === 'removeRow') {
                if (tableRows <= 1) {
                    throw new Error("Cannot remove the last row");
                }
                params.tableConfig = {
                    rowOperations: [{ operation: "remove", index: tableRows - 1 }]
                };
                setTableRows(prev => prev - 1);
            } else if (operation === 'addCol') {
                params.tableConfig = {
                    columnOperations: [{ operation: "insert", index: tableCols, count: 1 }]
                };
                setTableCols(prev => prev + 1);
            } else if (operation === 'removeCol') {
                if (tableCols <= 1) {
                    throw new Error("Cannot remove the last column");
                }
                params.tableConfig = {
                    columnOperations: [{ operation: "remove", index: tableCols - 1 }]
                };
                setTableCols(prev => prev - 1);
            }

            const tool = new CreateOrEditTableTool();
            const toolResult = await tool.execute(params);
            
            setResult({
                success: true,
                data: toolResult,
                timestamp: new Date().toLocaleTimeString(),
            });
        } catch (error) {
            setResult({
                success: false,
                data: error instanceof Error ? error.message : String(error),
                timestamp: new Date().toLocaleTimeString(),
            });
        } finally {
            setIsExecuting(false);
        }
    };

    if (!isVisible) {
        return null;
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <Text className={styles.title}>
                    🧪 Tool Test Panel {globalDevModeEnabled ? "(Dev Mode Active)" : "(Ctrl+Shift+T)"}
                </Text>
                <Button
                    appearance="transparent"
                    size="small"
                    onClick={() => {
                        // If dev mode is enabled, just hide temporarily (don't change dev mode)
                        if (globalDevModeEnabled) {
                            globalTestPanelVisible = false;
                            notifyTestPanelListeners();
                        } else {
                            toggleToolTestPanel();
                        }
                    }}
                    style={{ color: 'white' }}
                >
                    ✕
                </Button>
            </div>

            <div className={styles.content}>
                {/* Tool Selection */}
                <div>
                    <Text className={styles.label}>Tool</Text>
                    <Dropdown
                        value={selectedTool}
                        selectedOptions={[selectedTool]}
                        onOptionSelect={(_, data) => setSelectedTool(data.optionValue as ToolName)}
                    >
                        <Option value="createOrEditShape">createOrEditShape</Option>
                        <Option value="buildSection">buildSection</Option>
                        <Option value="createOrEditTable">📊 createOrEditTable</Option>
                        <Option value="createOrEditChart">📈 createOrEditChart</Option>
                        <Option value="geminiImageGen">🖼️ Gemini Image Generation Test</Option>
                    </Dropdown>
                </div>

                {/* Presets */}
                <div>
                    <Text className={styles.label}>Quick Presets</Text>
                    <div className={styles.presets}>
                        {Object.entries(PRESETS).map(([key, preset]) => (
                            <Button
                                key={key}
                                size="small"
                                appearance="outline"
                                className={styles.presetButton}
                                onClick={() => loadPreset(key)}
                            >
                                {preset.description}
                            </Button>
                        ))}
                    </div>
                </div>

                {/* Interactive Table Testing Section */}
                <div className={styles.tableTestSection}>
                    <div className={styles.tableTestHeader}>
                        <Table20Regular />
                        <Text className={styles.label} style={{ marginBottom: 0 }}>Interactive Table Testing (Auto-Resize)</Text>
                    </div>
                    
                    <div className={styles.tableTestRow}>
                        <Text style={{ fontSize: "11px", minWidth: "50px" }}>Slide:</Text>
                        <Input
                            type="number"
                            size="small"
                            value={String(tableSlideId)}
                            onChange={(_, data) => setTableSlideId(parseInt(data.value) || 1)}
                            className={styles.inputSmall}
                        />
                        <Text style={{ fontSize: "11px", minWidth: "50px" }}>Rows:</Text>
                        <Input
                            type="number"
                            size="small"
                            value={String(tableRows)}
                            onChange={(_, data) => setTableRows(parseInt(data.value) || 3)}
                            className={styles.inputSmall}
                        />
                        <Text style={{ fontSize: "11px", minWidth: "50px" }}>Cols:</Text>
                        <Input
                            type="number"
                            size="small"
                            value={String(tableCols)}
                            onChange={(_, data) => setTableCols(parseInt(data.value) || 4)}
                            className={styles.inputSmall}
                        />
                        <Text style={{ fontSize: "11px", minWidth: "60px" }}>Font Size:</Text>
                        <Input
                            type="number"
                            size="small"
                            value={String(tableFontSize)}
                            onChange={(_, data) => setTableFontSize(parseInt(data.value) || 14)}
                            className={styles.inputSmall}
                            style={{ width: "50px" }}
                        />
                    </div>

                    <div className={styles.tableTestButtons}>
                        <Button
                            size="small"
                            appearance="primary"
                            icon={<Table20Regular />}
                            onClick={createTestTable}
                            disabled={isExecuting}
                        >
                            Create Lorem Table ({tableFontSize}pt)
                        </Button>
                        <Button
                            size="small"
                            appearance="outline"
                            onClick={selectTableFromSlide}
                            disabled={isExecuting}
                        >
                            Select Table from Slide
                        </Button>
                    </div>

                    <Divider style={{ margin: "8px 0" }} />

                    <div className={styles.tableTestRow}>
                        <div className={styles.tableTestInfo}>
                            {tableShapeId 
                                ? `📊 Table ID: ${tableShapeId} | Current: ${tableRows}×${tableCols}`
                                : "⚠️ No table selected - create one or select from slide"
                            }
                        </div>
                        <Checkbox
                            checked={autoResize}
                            onChange={(_, data) => setAutoResize(data.checked === true)}
                            label={autoResize ? "Auto-resize ON" : "Auto-resize OFF"}
                            style={{ marginLeft: "auto" }}
                        />
                    </div>

                    <div className={styles.tableTestButtons}>
                        <Button
                            size="small"
                            appearance="outline"
                            icon={<Add20Regular />}
                            onClick={() => executeTableOperation('addRow')}
                            disabled={isExecuting || !tableShapeId}
                        >
                            Add Row
                        </Button>
                        <Button
                            size="small"
                            appearance="outline"
                            icon={<Subtract20Regular />}
                            onClick={() => executeTableOperation('removeRow')}
                            disabled={isExecuting || !tableShapeId || tableRows <= 1}
                        >
                            Remove Row
                        </Button>
                        <Button
                            size="small"
                            appearance="outline"
                            icon={<Add20Regular />}
                            onClick={() => executeTableOperation('addCol')}
                            disabled={isExecuting || !tableShapeId}
                        >
                            Add Column
                        </Button>
                        <Button
                            size="small"
                            appearance="outline"
                            icon={<Subtract20Regular />}
                            onClick={() => executeTableOperation('removeCol')}
                            disabled={isExecuting || !tableShapeId || tableCols <= 1}
                        >
                            Remove Column
                        </Button>
                    </div>

                    <Text style={{ fontSize: "10px", color: tokens.colorNeutralForeground3, marginTop: "4px" }}>
                        {autoResize 
                            ? "🔄 Auto-resize ON: Table stays same size, rows/cols shrink proportionally"
                            : "📐 Auto-resize OFF: Table expands when rows/cols are added"}
                    </Text>
                </div>

                {/* Table Style Copy Section */}
                <div className={styles.tableTestSection}>
                    <div className={styles.tableTestHeader}>
                        <Table20Regular />
                        <Text className={styles.label} style={{ marginBottom: 0 }}>Table Style Copy (COM)</Text>
                    </div>
                    
                    {/* Source Table */}
                    <div className={styles.tableTestRow}>
                        <Text style={{ fontSize: "11px", minWidth: "80px", fontWeight: 600 }}>Source:</Text>
                        <Text style={{ fontSize: "11px", minWidth: "35px" }}>Slide</Text>
                        <Input
                            type="number"
                            size="small"
                            value={String(sourceTableSlide)}
                            onChange={(_, data) => setSourceTableSlide(parseInt(data.value) || 1)}
                            className={styles.inputSmall}
                        />
                        <Text style={{ fontSize: "11px", minWidth: "50px" }}>Shape</Text>
                        <Input
                            type="number"
                            size="small"
                            value={sourceTableShape ? String(sourceTableShape) : ''}
                            onChange={(_, data) => setSourceTableShape(data.value ? parseInt(data.value) : null)}
                            className={styles.inputSmall}
                            placeholder="ID"
                        />
                        <Button
                            size="small"
                            appearance="outline"
                            onClick={() => getSelectedTableInfo('source')}
                            disabled={isExecuting}
                        >
                            Get Selected
                        </Button>
                    </div>

                    {/* Target Table */}
                    <div className={styles.tableTestRow}>
                        <Text style={{ fontSize: "11px", minWidth: "80px", fontWeight: 600 }}>Target:</Text>
                        <Text style={{ fontSize: "11px", minWidth: "35px" }}>Slide</Text>
                        <Input
                            type="number"
                            size="small"
                            value={String(targetTableSlide)}
                            onChange={(_, data) => setTargetTableSlide(parseInt(data.value) || 1)}
                            className={styles.inputSmall}
                        />
                        <Text style={{ fontSize: "11px", minWidth: "50px" }}>Shape</Text>
                        <Input
                            type="number"
                            size="small"
                            value={targetTableShape ? String(targetTableShape) : ''}
                            onChange={(_, data) => setTargetTableShape(data.value ? parseInt(data.value) : null)}
                            className={styles.inputSmall}
                            placeholder="ID"
                        />
                        <Button
                            size="small"
                            appearance="outline"
                            onClick={() => getSelectedTableInfo('target')}
                            disabled={isExecuting}
                        >
                            Get Selected
                        </Button>
                    </div>

                    {extractedStyleInfo && (
                        <div className={styles.tableTestInfo} style={{ backgroundColor: tokens.colorPaletteGreenBackground1 }}>
                            ✓ Style extracted: {extractedStyleInfo}
                        </div>
                    )}

                    <div className={styles.tableTestButtons}>
                        <Button
                            size="small"
                            appearance="primary"
                            onClick={extractTableStyle}
                            disabled={isExecuting || !sourceTableShape}
                        >
                            Extract Style
                        </Button>
                        <Button
                            size="small"
                            appearance="primary"
                            style={{ backgroundColor: tokens.colorPaletteGreenBackground3 }}
                            onClick={applyTableStyle}
                            disabled={isExecuting || !sourceTableShape || !targetTableShape}
                        >
                            Copy Style to Target
                        </Button>
                    </div>

                    <Text style={{ fontSize: "10px", color: tokens.colorNeutralForeground3, marginTop: "4px" }}>
                        1. Select source table → Get Selected → Extract Style  |  2. Select target → Get Selected → Copy Style
                    </Text>
                </div>

                {/* JSON Input */}
                <div className={styles.textareaWrapper}>
                    <Text className={styles.label}>Parameters (JSON)</Text>
                    <Textarea
                        className={styles.textarea}
                        value={inputJson}
                        onChange={(_, data) => setInputJson(data.value)}
                        resize="vertical"
                        style={{ flex: 1, minHeight: "280px" }}
                    />
                </div>

                {/* Result */}
                {result && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text className={styles.label}>
                                Result ({result.timestamp}) - {result.success ? '✅ Success' : '❌ Error'}
                            </Text>
                            <Button
                                size="small"
                                appearance="subtle"
                                icon={<Copy20Regular />}
                                onClick={copyResult}
                            />
                        </div>
                        
                        {/* Image preview for Gemini results */}
                        {selectedTool === "geminiImageGen" && result.success && (result.data as GeminiTestResult)?.image_base64 && (
                            <div style={{ marginBottom: '12px', textAlign: 'center' }}>
                                <Text className={styles.label}>Generated Image Preview</Text>
                                <div style={{ 
                                    border: `1px solid ${tokens.colorNeutralStroke1}`, 
                                    borderRadius: '4px', 
                                    padding: '8px',
                                    backgroundColor: '#fff'
                                }}>
                                    <img 
                                        src={`data:image/png;base64,${(result.data as GeminiTestResult).image_base64}`}
                                        alt="Generated slide reference"
                                        style={{ maxWidth: '100%', maxHeight: '300px' }}
                                    />
                                </div>
                            </div>
                        )}
                        
                        {/* Gemini test summary */}
                        {selectedTool === "geminiImageGen" && (() => {
                            const geminiResult = result.data as GeminiTestResult;
                            return (
                                <div style={{ marginBottom: '8px', padding: '8px', backgroundColor: tokens.colorNeutralBackground3, borderRadius: '4px' }}>
                                    <Text style={{ fontSize: '11px' }}>
                                        <strong>Model:</strong> {geminiResult?.model_used || 'unknown'}<br/>
                                        <strong>Image Generated:</strong> {geminiResult?.image_base64 ? '✅ Yes' : '❌ No'}<br/>
                                        {geminiResult?.error && (
                                            <><strong>Error:</strong> {geminiResult.error}<br/></>
                                        )}
                                        {geminiResult?.text_response && (
                                            <><strong>Text Response:</strong> {geminiResult.text_response.substring(0, 200)}...<br/></>
                                        )}
                                    </Text>
                                </div>
                            );
                        })()}
                        
                        <div className={`${styles.resultArea} ${result.success ? styles.successResult : styles.errorResult}`}>
                            {JSON.stringify(result.data, null, 2)}
                        </div>

                        {/* Screenshot comparison */}
                        {lastTestRecord && (lastTestRecord.beforeScreenshot || lastTestRecord.afterScreenshot) && (
                            <div style={{ marginTop: '12px' }}>
                                <Text className={styles.label}>Before / After Screenshots</Text>
                                <div style={{ 
                                    display: 'flex', 
                                    gap: '8px', 
                                    marginTop: '8px',
                                    justifyContent: 'space-between'
                                }}>
                                    {lastTestRecord.beforeScreenshot && (
                                        <div style={{ flex: 1, textAlign: 'center' }}>
                                            <Text style={{ fontSize: '10px', color: tokens.colorNeutralForeground3 }}>BEFORE</Text>
                                            <div style={{ 
                                                border: `1px solid ${tokens.colorNeutralStroke1}`, 
                                                borderRadius: '4px', 
                                                padding: '4px',
                                                backgroundColor: '#fff',
                                                marginTop: '4px'
                                            }}>
                                                <img 
                                                    src={`data:image/png;base64,${lastTestRecord.beforeScreenshot}`}
                                                    alt="Before"
                                                    style={{ maxWidth: '100%', maxHeight: '150px' }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {lastTestRecord.afterScreenshot && (
                                        <div style={{ flex: 1, textAlign: 'center' }}>
                                            <Text style={{ fontSize: '10px', color: tokens.colorNeutralForeground3 }}>AFTER</Text>
                                            <div style={{ 
                                                border: `1px solid ${tokens.colorNeutralStroke1}`, 
                                                borderRadius: '4px', 
                                                padding: '4px',
                                                backgroundColor: '#fff',
                                                marginTop: '4px'
                                            }}>
                                                <img 
                                                    src={`data:image/png;base64,${lastTestRecord.afterScreenshot}`}
                                                    alt="After"
                                                    style={{ maxWidth: '100%', maxHeight: '150px' }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* OJS Logs */}
                        {lastTestRecord && lastTestRecord.ojsLogs.length > 0 && (
                            <div style={{ marginTop: '12px' }}>
                                <Text className={styles.label}>Office.js Logs ({lastTestRecord.ojsLogs.length})</Text>
                                <div className={styles.resultArea} style={{ maxHeight: '100px', fontSize: '10px' }}>
                                    {lastTestRecord.ojsLogs.slice(-20).join('\n')}
                                </div>
                            </div>
                        )}

                        {/* COM Logs */}
                        {lastTestRecord && lastTestRecord.comLogs.length > 0 && (
                            <div style={{ marginTop: '12px' }}>
                                <Text className={styles.label}>Python COM Logs ({lastTestRecord.comLogs.length})</Text>
                                <div className={styles.resultArea} style={{ maxHeight: '100px', fontSize: '10px' }}>
                                    {lastTestRecord.comLogs.slice(-20).join('\n')}
                                </div>
                            </div>
                        )}

                        {/* Test duration */}
                        {lastTestRecord && (
                            <div style={{ marginTop: '8px', fontSize: '11px', color: tokens.colorNeutralForeground3 }}>
                                Duration: {lastTestRecord.durationMs}ms | Test ID: {lastTestRecord.testId}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className={styles.actions}>
                <Button
                    appearance="primary"
                    icon={<Play20Regular />}
                    onClick={executeTest}
                    disabled={isExecuting}
                >
                    {isExecuting ? "Executing..." : "Execute Tool"}
                </Button>
                <Checkbox
                    checked={captureScreenshots}
                    onChange={(_, data) => setCaptureScreenshots(data.checked === true)}
                    label={
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Camera20Regular />
                            Screenshots
                        </span>
                    }
                />
                <Button
                    appearance="subtle"
                    icon={<ArrowDownload20Regular />}
                    onClick={exportTestResults}
                    disabled={!lastTestRecord}
                    title="Export test results as JSON"
                >
                    Export
                </Button>
                <Button
                    appearance="subtle"
                    icon={<Delete20Regular />}
                    onClick={() => {
                        setInputJson("{}");
                        setResult(null);
                        setLastTestRecord(null);
                    }}
                >
                    Clear
                </Button>
            </div>
        </div>
    );
};

export default ToolTestPanel;
